import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { ChevronDown, Compass, MapPinned, Briefcase, Mail, Route as RouteIcon } from "lucide-react";
import { MAIN_LINKS, TRIP_LINKS, PARTNER_LINKS, MENU_LINKS, showsSiteNav, isActive } from "../config/siteNav";
import { useAuth } from "../context/AuthContext";
import CoBrandLogo from "./partner/CoBrandLogo";
import AgentNavButton from "./agent/AgentNavButton";
import AgentAuthModal from "./agent/AgentAuthModal";
import { useAgentSession } from "../utils/agentAuth";
import "../utils/agentIdentityBridge"; // stamps partner identity on all API calls + re-verifies session
import "../styles/Navbar.css"; // Import the new styles

const Navbar = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, openLoginModal, handleLogout: logoutFromContext } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [agentModalOpen, setAgentModalOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState("");   // desktop dropdown open: "trips" | "partners" (also opens on hover)
    const agentSession = useAgentSession();

    // Close menus on navigation
    useEffect(() => { setOpenMenu(""); setIsMobileMenuOpen(false); }, [location.pathname]);

    useEffect(() => {
        if (location.pathname !== "/" && location.pathname !== "/plan-a-trip") {
            setIsScrolled(true);
            return;
        }

        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        // Initial check
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [location.pathname]);

    const handleLogout = async () => {
        try {
            await logoutFromContext();

            // Redirect to Home ONLY if on a private/account-related page
            const privatePaths = ["/booking-history", "/booking-detail", "/book-flight", "/booking-flight"];
            const isPrivatePath = privatePaths.some(path => location.pathname.startsWith(path));

            if (isPrivatePath) {
                navigate("/");
            }

            // Reload to clear all states and stay on the current URL (if public) or Home (if private)
            window.location.reload();
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const isChatPage = location.pathname === "/chat";
    // The Plan a Trip page is the home page on its Plan tab, so it gets the same header
    const isHomePage = location.pathname === "/" || location.pathname === "/plan-a-trip";
    // Content pages get the full navigation (src/config/siteNav.js); booking flows and dashboards don't
    const siteNav = showsSiteNav(location.pathname);
    const path = location.pathname;
    const isPartnerPage = location.pathname.startsWith("/partner");

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    return (
        <nav className={`top-nav ${isScrolled ? 'is-scrolled' : 'is-transparent'}`}>
            {/* RouteChef logo, or partner branding "with Routechef" when a partner is signed in
                or the customer came from a partner's link */}
            <div className="nav-brand">
                <CoBrandLogo />
            </div>

            <div className="nav-actions" style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                {siteNav && (
                    <div className="nav-links hidden lg:flex">
                        {MAIN_LINKS.map((l) => l.menu ? (
                            <div key={l.key} className={`nav-drop${openMenu === l.menu ? " open" : ""}`}
                                onMouseLeave={() => setOpenMenu("")}>
                                <button type="button" className={`nav-link nav-drop-btn${MENU_LINKS[l.menu].some((t) => isActive(path, t.to)) ? " active" : ""}`}
                                    aria-expanded={openMenu === l.menu} aria-haspopup="true" onClick={() => setOpenMenu((v) => (v === l.menu ? "" : l.menu))}>
                                    {l.label} <ChevronDown size={15} aria-hidden="true" />
                                </button>
                                {/* Always in the page (shown on hover, focus or click) so crawlers see the links */}
                                <div className="nav-drop-panel" role="menu">
                                    {MENU_LINKS[l.menu].map((t) => (
                                        <Link key={t.to} to={t.to} role="menuitem" className={isActive(path, t.to) ? "active" : ""} onClick={() => setOpenMenu("")}>
                                            <span className="nav-drop-label">{t.label}</span>
                                            <span className="nav-drop-sub">{t.sub}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <Link key={l.to} to={l.to} className={`nav-link${isActive(path, l.to) ? " active" : ""}`} aria-current={isActive(path, l.to) ? "page" : undefined}>
                                {l.label}
                            </Link>
                        ))}

                        <AgentNavButton
                            onOpenLogin={() => setAgentModalOpen(true)}
                        />

                        {isHomePage && userProfile.code === 3 && !agentSession && (
                            <button
                                className="signin-nav-btn"
                                onClick={() => openLoginModal()}
                            >
                                Sign In
                            </button>
                        )}
                    </div>
                )}

                {children}

                {/* Other pages: only signed-in partners get a shortcut back to their dashboard */}
                {!siteNav && !isPartnerPage && agentSession && (
                    <>
                        <div className="hidden sm:block">
                            <AgentNavButton />
                        </div>

                        <div className="sm:hidden">
                            <AgentNavButton variant="icon" />
                        </div>
                    </>
                )}

                {isHomePage ? (
                    // On home page: show avatar-only dropdown if logged in, nothing if not
                    userProfile.code === 2 ? (
                        <div
                            className="user-profile-wrapper"
                            style={{ position: "relative" }}
                        >
                            <Menu as="div" className="user-dropdown">
                                <Menu.Button
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "4px",
                                        borderRadius: "50%"
                                    }}
                                    className="transition-colors"
                                >
                                    {userProfile.photoURL &&
                                    userProfile.photoURL !== '/profileDefault.png' ? (
                                        <img
                                            src={userProfile.photoURL}
                                            alt="Profile"
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                objectFit: "cover"
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.querySelector(
                                                    '.avatar-fallback-home'
                                                ).style.display = 'flex';
                                            }}
                                        />
                                    ) : null}

                                    <div
                                        className="avatar-fallback-home"
                                        style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#FF5B15",
                                            color: "white",
                                            display:
                                                (userProfile.photoURL &&
                                                userProfile.photoURL !== '/profileDefault.png')
                                                    ? 'none'
                                                    : 'flex',
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        {userProfile.email ? (
                                            userProfile.displayName?.charAt(0) || 'U'
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        )}
                                    </div>
                                </Menu.Button>

                                <Menu.Items
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        marginTop: "4px",
                                        width: "180px",
                                        backgroundColor: "white",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                        padding: "4px 0",
                                        zIndex: 1001
                                    }}
                                >
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/booking-history"
                                                style={{
                                                    display: "block",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    textDecoration: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent"
                                                }}
                                            >
                                                My Bookings
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={handleLogout}
                                                style={{
                                                    width: "100%",
                                                    textAlign: "left",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#EF4444",
                                                    border: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                Logout
                                            </button>
                                        )}
                                    </Menu.Item>
                                </Menu.Items>
                            </Menu>
                        </div>
                    ) : null
                ) : (
                    // On all other pages: existing behavior (profile + name, or sign-in button)
                    userProfile.code === 2 ? (
                        <div
                            className="user-profile-wrapper"
                            style={{ position: "relative" }}
                        >
                            <Menu as="div" className="user-dropdown">
                                <Menu.Button
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "4px 8px",
                                        borderRadius: "8px"
                                    }}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    <span
                                        className="user-name"
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#374151",
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {userProfile.displayName}
                                    </span>

                                    {userProfile.photoURL &&
                                    userProfile.photoURL !== '/profileDefault.png' ? (
                                        <img
                                            src={userProfile.photoURL}
                                            alt="Profile"
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                objectFit: "cover"
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.querySelector(
                                                    '.avatar-fallback'
                                                ).style.display = 'flex';
                                            }}
                                        />
                                    ) : null}

                                    <div
                                        className="avatar-fallback"
                                        style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#FF5B15",
                                            color: "white",
                                            display:
                                                (userProfile.photoURL &&
                                                userProfile.photoURL !== '/profileDefault.png')
                                                    ? 'none'
                                                    : 'flex',
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        {userProfile.email ? (
                                            userProfile.displayName?.charAt(0) || 'U'
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20 21v-2a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        )}
                                    </div>
                                </Menu.Button>

                                <Menu.Items
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        marginTop: "4px",
                                        width: "180px",
                                        backgroundColor: "white",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                        padding: "4px 0",
                                        zIndex: 1001
                                    }}
                                >
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/booking-history"
                                                style={{
                                                    display: "block",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    textDecoration: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent"
                                                }}
                                            >
                                                My Bookings
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={handleLogout}
                                                style={{
                                                    width: "100%",
                                                    textAlign: "left",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#EF4444",
                                                    border: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                Logout
                                            </button>
                                        )}
                                    </Menu.Item>
                                </Menu.Items>
                            </Menu>
                        </div>
                    ) : userProfile.code === 3 && !agentSession ? (
                        <button
                            className="signin-nav-btn"
                            onClick={() => openLoginModal()}
                        >
                            Sign In
                        </button>
                    ) : null
                )}

                {/* Phone and tablet menu: the same key links as the desktop bar */}
                {siteNav && (
                    <div style={{ position: "relative" }}>
                        <button
                            className="mobile-menu-btn lg:hidden"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="mobile-menu-icon" aria-hidden="true">{isMobileMenuOpen ? "✕" : "☰"}</span>
                        </button>

                        {isMobileMenuOpen && (
                            <>
                                <div className="mobile-dropdown-scrim lg:hidden" onClick={closeMobileMenu} aria-hidden="true" />
                                <div className="mobile-dropdown lg:hidden" role="menu">
                                    <Link to="/plan-a-trip" onClick={closeMobileMenu} className={isActive(path, "/plan-a-trip") ? "active" : ""}>
                                        <Compass size={17} aria-hidden="true" /> Plan a Trip
                                    </Link>
                                    <Link to="/" onClick={closeMobileMenu} className={path === "/" ? "active" : ""}>
                                        <RouteIcon size={17} aria-hidden="true" /> Train, flight and bus routes
                                    </Link>

                                    <div className="mobile-dropdown-head">Trips</div>
                                    {TRIP_LINKS.map((t) => (
                                        <Link key={t.to} to={t.to} onClick={closeMobileMenu} className={`mobile-trip${isActive(path, t.to) ? " active" : ""}`}>
                                            <span className="nav-drop-label">{t.label}</span>
                                            <span className="nav-drop-sub">{t.sub}</span>
                                        </Link>
                                    ))}

                                    <div className="mobile-dropdown-sep" />
                                    <Link to="/how-to-reach" onClick={closeMobileMenu} className={isActive(path, "/how-to-reach") ? "active" : ""}>
                                        <MapPinned size={17} aria-hidden="true" /> How to Reach
                                    </Link>
                                    <div className="mobile-dropdown-head"><Briefcase size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />For Partners</div>
                                    {PARTNER_LINKS.map((t) => (
                                        <Link key={t.to} to={t.to} onClick={closeMobileMenu} className={`mobile-trip${isActive(path, t.to) ? " active" : ""}`}>
                                            <span className="nav-drop-label">{t.label}</span>
                                            <span className="nav-drop-sub">{t.sub}</span>
                                        </Link>
                                    ))}
                                    <Link to="/contact" onClick={closeMobileMenu} className={isActive(path, "/contact") ? "active" : ""}>
                                        <Mail size={17} aria-hidden="true" /> Contact
                                    </Link>

                                    <div className="mobile-dropdown-sep" />
                                    <AgentNavButton
                                        variant="menu"
                                        onNavigate={closeMobileMenu}
                                        onOpenLogin={() => setAgentModalOpen(true)}
                                    />

                                    {isHomePage && userProfile.code === 3 && !agentSession && (
                                        <button
                                            className="mobile-nav-link-btn"
                                            onClick={() => {
                                                closeMobileMenu();
                                                openLoginModal();
                                            }}
                                            style={{
                                                color: "#FF5B15",
                                                fontWeight: "600"
                                            }}
                                        >
                                            Sign In
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Owned here so it survives the mobile dropdown closing */}
            <AgentAuthModal
                open={agentModalOpen}
                onClose={() => setAgentModalOpen(false)}
            />

            {/* Phones: brand shrinks (ellipsis) so the profile avatar is never pushed off screen */}
            <style>{`
                .top-nav .nav-brand { min-width: 0; flex: 0 1 auto; overflow: hidden; display: flex; align-items: center; }
                .top-nav .nav-actions { flex-shrink: 0; }
                @media (max-width: 640px) {
                    .top-nav .nav-actions { gap: 0.6rem !important; }
                    .top-nav .user-name { display: none; }
                    .top-nav .nav-brand .cbl-name { max-width: 120px !important; }
                }
                @media (max-width: 380px) {
                    .top-nav .nav-brand .cbl-name { max-width: 90px !important; }
                }
            `}</style>
        </nav>
    );
};

export default Navbar;
